import { all, call, put, takeLatest } from 'redux-saga/effects'
import {
  setLoading,
  unsetLoading,
} from 'portal-modules/common/redux/modules/loading'
import { getBudgetItemInvoices, getProjectBudgetSummary } from '../../api'
import { allowanceLoadingKey } from '../../constants/display-content'

const PREFIX = 'tenantImprovements'
export const FETCH_PROJECT_ALLOWANCE_REQUEST = `${PREFIX}/FETCH_PROJECT_ALLOWANCE_REQUEST`
export const FETCH_PROJECT_ALLOWANCE_SUCCESS = `${PREFIX}/FETCH_PROJECT_ALLOWANCE_SUCCESS`
export const FETCH_PROJECT_ALLOWANCE_FAILURE = `${PREFIX}/FETCH_PROJECT_ALLOWANCE_FAILURE`

export const fetchProjectAllowanceRequest = (id) => ({
  type: FETCH_PROJECT_ALLOWANCE_REQUEST,
  payload: { id },
})

export const fetchProjectAllowanceSuccess = (id, allowancePaid) => ({
  type: FETCH_PROJECT_ALLOWANCE_SUCCESS,
  payload: { id, allowancePaid },
})

export const fetchProjectAllowanceFailure = (id, error) => ({
  type: FETCH_PROJECT_ALLOWANCE_FAILURE,
  payload: { id, error },
})

const initialState = { byId: {} }

const updateEntry = (state, id, patch) => ({
  byId: { ...state.byId, [id]: { ...state.byId[id], ...patch } },
})

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_PROJECT_ALLOWANCE_REQUEST: {
      const { id } = action.payload
      return updateEntry(state, id, { isLoading: true, error: null })
    }
    case FETCH_PROJECT_ALLOWANCE_SUCCESS: {
      const { id, allowancePaid } = action.payload
      return updateEntry(state, id, {
        allowancePaid,
        isLoading: false,
        error: null,
      })
    }
    case FETCH_PROJECT_ALLOWANCE_FAILURE: {
      const { id, error } = action.payload
      return updateEntry(state, id, { isLoading: false, error })
    }
    default:
      return state
  }
}

export default reducer

// Defensive optional chaining mirrors the sibling selectors in projects.js.
// On direct-load / refresh of the detail route the slice may not yet be
// registered, and we must not throw a TypeError before the reducer wires up.
const DEFAULT_ALLOWANCE_ENTRY = {
  allowancePaid: 0,
  isLoading: false,
  error: null,
}

export const selectAllowanceByProjectId = (id) => (state) =>
  state?.tenantImprovementProjectAllowance?.byId?.[id] ?? DEFAULT_ALLOWANCE_ENTRY

// Re-exported alias for clarity at call sites; the canonical builder lives
// in constants/display-content so tests, route loaders, and the saga all
// agree on the key shape.
const loadingKeyFor = allowanceLoadingKey

// "Allowance Paid" = sum of every invoice on every budget item where the
// invoice is of allowance type. The backend models this as
// `expense_type_id === 1` (allowance) vs `2` (non-allowance), matching how
// tenant-improvement-ui derives the same value (BudgetApiService.getInvoices
// + ManageInvoicesModalV2's filter(type==='allowance').reduce(sum)).
//
// /projects/{id}/budget-summary-financial exposes `budget_items[]` with
// per-item ids, but no allowance/non-allowance breakdown. We fan out to
// /budget-items/{itemId}/invoices/financial for each item, then sum.
const ALLOWANCE_EXPENSE_TYPE_ID = 1
// Legacy invoice payload shape returned by older backend versions / mocks
// that lack `expense_type_id`. Kept until the backend can guarantee the
// new shape on every endpoint that returns invoices.
const ALLOWANCE_TYPE_LEGACY = 'allowance'

const isAllowanceInvoice = (invoice) => {
  if (!invoice || typeof invoice !== 'object') return false
  if (invoice.expense_type_id != null) {
    return Number(invoice.expense_type_id) === ALLOWANCE_EXPENSE_TYPE_ID
  }
  return invoice.type === ALLOWANCE_TYPE_LEGACY
}

const sumAllowanceAmount = (invoices) => {
  if (!Array.isArray(invoices)) return 0
  return invoices.reduce(
    (acc, invoice) =>
      isAllowanceInvoice(invoice) ? acc + (Number(invoice.amount) || 0) : acc,
    0,
  )
}

const extractInvoices = (response) => {
  const body = response?.data
  if (Array.isArray(body)) return body
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body?.invoices)) return body.invoices
  return []
}

const extractBudgetItemIds = (response) => {
  const body = response?.data || {}
  const items = body.budget_items || body.summary?.budget_items
  if (!Array.isArray(items)) return []
  return items.map((item) => item?.id).filter((id) => id != null)
}

export function* fetchProjectAllowanceSaga(action) {
  const { id } = action.payload
  // Refuse to fan out to the API without an id. Without this guard, a
  // malformed dispatch (or a future feature-flag-gated dispatch) would
  // both 1) hit /projects/undefined/budget-summary-financial against the
  // backend and 2) leak a `tenant-improvements-allowance-undefined`
  // loading key. The dispatcher is the single legitimate caller today
  // and always has an id, so this branch is purely defensive — and is
  // returned BEFORE setLoading specifically so no loading key is ever
  // registered for a missing id.
  if (id == null) return
  yield put(
    setLoading({ key: loadingKeyFor(id), message: __('Loading allowance…') }),
  )
  try {
    const summaryResponse = yield call(getProjectBudgetSummary, id)
    const itemIds = extractBudgetItemIds(summaryResponse)

    let allowancePaid = 0
    if (itemIds.length > 0) {
      // Known limitation (perf): unbounded fan-out — one invoice fetch
      // per budget item, all in flight in parallel. Today's projects
      // ship with a small N (typically <10 items), so this is fine.
      // If/when projects start carrying dozens of budget items we
      // should either:
      //   (a) cap parallelism with a small bounded-parallel saga
      //       helper (e.g., 5 in flight at a time), or
      //   (b) ask the backend to extend
      //       /projects/{id}/budget-summary-financial with per-item
      //       allowance totals so the client never needs to fan out.
      // (b) is the structural fix; (a) is the safety net. No follow-up
      // ticket is filed yet; do not treat this comment as a tracked
      // commitment — file a ticket and reference it here before
      // relying on this work being scheduled.
      const invoiceResponses = yield all(
        itemIds.map((itemId) => call(getBudgetItemInvoices, itemId)),
      )
      allowancePaid = invoiceResponses.reduce(
        (total, response) => total + sumAllowanceAmount(extractInvoices(response)),
        0,
      )
    }

    yield put(fetchProjectAllowanceSuccess(id, allowancePaid))
  } catch (error) {
    // Never propagate raw error.message to user-facing Redux state: backend
    // / transport errors can leak internal paths, service names, CORS
    // details, etc. Log the raw error in non-production for debugging, and
    // put a fixed, translated message in state. Mirrors fetchProjectsSaga.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('fetchProjectAllowanceSaga failed', error)
    }
    yield put(
      fetchProjectAllowanceFailure(id, __('Failed to load allowance')),
    )
  } finally {
    yield put(unsetLoading({ key: loadingKeyFor(id) }))
  }
}

function* watchFetchProjectAllowance() {
  yield takeLatest(FETCH_PROJECT_ALLOWANCE_REQUEST, fetchProjectAllowanceSaga)
}

export const sagas = [watchFetchProjectAllowance]
